<template>
    <transition name="disable-modal">
        <div v-if="opened" class="disable-modal" role="presentation">
            <div class="disable-modal__backdrop" @click="onKeepEnabled" />
            <section
                class="disable-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="disable-auto-renewal-title"
                @click.stop
            >
                <h2 id="disable-auto-renewal-title" class="disable-modal__title">{{ title }}</h2>
                <p class="disable-modal__lead">{{ leadText }}</p>

                <div v-if="step === 1" class="disable-modal__reason-list">
                    <label
                        v-for="reason in reasons"
                        :key="reason.value"
                        class="disable-modal__reason"
                        :class="{ 'disable-modal__reason--selected': selectedReason === reason.value }"
                    >
                        <input v-model="selectedReason" type="radio" name="disable-reason" :value="reason.value" />
                        <span>{{ reason.label }}</span>
                    </label>
                </div>

                <div v-else class="disable-modal__confirm">
                    <div class="disable-modal__confirm-box">
                        <strong>{{ consequenceTitle }}</strong>
                        <span>{{ consequenceHint }}</span>
                    </div>
                    <label class="disable-modal__confirm-check">
                        <input v-model="confirmed" type="checkbox" />
                        <span>Я понимаю последствия и хочу отключить автопродление.</span>
                    </label>
                </div>

                <div class="disable-modal__actions">
                    <button
                        type="button"
                        class="disable-modal__btn disable-modal__btn--secondary"
                        :disabled="loading"
                        @click="onKeepEnabled"
                    >
                        Оставить включённым
                    </button>
                    <button
                        v-if="step === 1"
                        type="button"
                        class="disable-modal__btn disable-modal__btn--primary"
                        :disabled="!selectedReason || loading"
                        @click="step = 2"
                    >
                        Продолжить
                    </button>
                    <button
                        v-else
                        type="button"
                        class="disable-modal__btn disable-modal__btn--danger"
                        :disabled="!confirmed || loading"
                        @click="$emit('confirm', selectedReason)"
                    >
                        {{ loading ? 'Отключаем…' : 'Отключить автопродление' }}
                    </button>
                </div>
            </section>
        </div>
    </transition>
</template>

<script>
import { SubscriptionCancellationReason } from '@/api/marketplace'

const REASONS = [
    { value: SubscriptionCancellationReason.WontUse, label: 'Не планируем пользоваться продуктом' },
    { value: SubscriptionCancellationReason.PaymentMethod, label: 'Не подходит способ оплаты' },
    { value: SubscriptionCancellationReason.Other, label: 'Другая причина' },
]

export default {
    name: 'CancelSubscriptionSidebarContent',

    props: {
        opened: {
            type: Boolean,
            default: false,
        },
        isTariffCancelMode: {
            type: Boolean,
            default: false,
        },
        isModuleCancelMode: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        withTariffRenewal: {
            type: Boolean,
            default: false,
        },
        channelDisplayName: {
            type: String,
            default: '',
        },
        periodTo: {
            type: String,
            default: '',
        },
    },

    data: () => ({
        step: 1,
        selectedReason: '',
        confirmed: false,
        reasons: REASONS,
    }),

    computed: {
        displayName() {
            return String(this.channelDisplayName || '').trim()
        },
        formattedPeriodTo() {
            return this.formatPeriodTo(this.periodTo)
        },
        title() {
            if (this.isTariffCancelMode) return 'Отключение автопродления тарифа'
            if (this.isModuleCancelMode) {
                return this.displayName
                    ? `Отключение автопродления модуля «${this.displayName}»`
                    : 'Отключение автопродления модуля'
            }
            return this.displayName
                ? `Отключение автопродления канала «${this.displayName}»`
                : 'Отключение автопродления канала'
        },
        leadText() {
            return 'Укажите причину. Это поможет улучшить условия подписки.'
        },
        consequenceTitle() {
            const date = this.formattedPeriodTo
            const name = this.displayName ? `«${this.displayName}»` : ''
            if (this.isTariffCancelMode) {
                return date
                    ? `После ${date} тариф не продлится автоматически`
                    : 'Тариф не продлится автоматически'
            }
            if (date && name) return `После ${date} ${name} отключится`
            if (date) return `После ${date} отключится`
            if (name) return `${name} отключится`
            return 'Отключится'
        },
        consequenceHint() {
            return 'После окончания оплаченного периода доступ может быть ограничен.'
        },
    },

    watch: {
        opened: {
            immediate: true,
            handler(isOpen) {
                if (isOpen) {
                    this.step = 1
                    this.selectedReason = ''
                    this.confirmed = false
                    this.$nextTick(() => {
                        const input = this.$el && this.$el.querySelector
                            ? this.$el.querySelector('input[name="disable-reason"]')
                            : null
                        if (input && typeof input.focus === 'function') input.focus()
                    })
                    return
                }
                this.step = 1
                this.selectedReason = ''
                this.confirmed = false
            },
        },
        step() {
            this.confirmed = false
        },
    },

    methods: {
        formatPeriodTo(value) {
            const raw = String(value || '').trim()
            if (!raw) return ''
            const parsed = this.parsePeriodToDate(raw)
            if (!parsed) return raw
            const d = String(parsed.getDate()).padStart(2, '0')
            const m = String(parsed.getMonth() + 1).padStart(2, '0')
            const y = String(parsed.getFullYear()).slice(-2)
            return `${d}.${m}.${y}`
        },
        parsePeriodToDate(periodTo) {
            if (!periodTo) return null
            const raw = String(periodTo).trim()
            const ymdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (ymdMatch) {
                const date = new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]))
                return Number.isNaN(date.getTime()) ? null : date
            }
            const parts = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2}|\d{4})$/)
            if (parts) {
                const day = Number(parts[1])
                const month = Number(parts[2]) - 1
                const year = parts[3].length === 2 ? 2000 + Number(parts[3]) : Number(parts[3])
                const date = new Date(year, month, day)
                return Number.isNaN(date.getTime()) ? null : date
            }
            const fallback = new Date(raw)
            return Number.isNaN(fallback.getTime()) ? null : fallback
        },
        onKeepEnabled() {
            if (this.loading) return
            this.$emit('back')
        },
    },
}
</script>

<style lang="sass" scoped>
.disable-modal
    position: fixed
    inset: 0
    z-index: 2100
    display: flex
    align-items: center
    justify-content: center
    padding: 16px

.disable-modal__backdrop
    position: absolute
    inset: 0
    background: rgba(23, 31, 48, .68)

.disable-modal__dialog
    position: relative
    z-index: 1
    box-sizing: border-box
    width: min(460px, 100%)
    padding: 24px
    border: 1px solid rgba(26, 26, 46, .10)
    border-radius: 20px
    background: #fff
    box-shadow: 0 24px 64px rgba(26, 26, 46, .18)
    color: #1A1A2E

.disable-modal__title
    margin: 0 0 8px
    font-size: 18px
    font-weight: 800
    line-height: 1.3
    letter-spacing: -.02em

.disable-modal__lead
    margin: 0 0 16px
    color: rgba(26, 26, 46, .66)
    font-size: 14px
    line-height: 1.5

.disable-modal__reason-list
    display: grid
    gap: 8px

.disable-modal__reason
    display: flex
    align-items: flex-start
    gap: 10px
    padding: 12px
    border: 1px solid rgba(26, 26, 46, .10)
    border-radius: 12px
    cursor: pointer
    font-size: 14px
    font-weight: 600
    line-height: 1.5
    input
        width: 18px
        height: 18px
        margin: 2px 0 0
        flex-shrink: 0
        accent-color: #525AD5
    &--selected
        border-color: #525AD5
        background: #EEF0FF

.disable-modal__confirm-box
    padding: 12px
    border: 1px solid rgba(232, 24, 60, .18)
    border-radius: 12px
    background: #FFE8ED
    strong,
    span
        display: block
    strong
        margin-bottom: 4px
        color: #8E1931
        font-size: 14px
        font-weight: 800
        line-height: 1.4
    span
        color: #8E1931
        font-size: 12px
        font-weight: 500
        line-height: 1.5

.disable-modal__confirm-check
    display: flex
    align-items: flex-start
    gap: 10px
    margin-top: 12px
    font-size: 14px
    font-weight: 600
    line-height: 1.5
    cursor: pointer
    input
        width: 18px
        height: 18px
        margin: 2px 0 0
        flex-shrink: 0
        accent-color: #E8183C

.disable-modal__actions
    display: grid
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr)
    gap: 8px
    margin-top: 20px

.disable-modal__btn
    min-height: 44px
    width: 100%
    min-width: 0
    padding: 0 16px
    border: 1px solid transparent
    border-radius: 12px
    font-size: 14px
    font-weight: 700
    line-height: 1.3
    white-space: normal
    cursor: pointer
    transition: transform .14s ease, background .14s ease, border-color .14s ease, box-shadow .14s ease
    &:not(:disabled):hover
        transform: translateY(-1px)
    &:disabled
        cursor: not-allowed
        opacity: .46
        transform: none
        box-shadow: none
    &--primary
        background: #525AD5
        color: #fff
        box-shadow: 0 8px 24px rgba(82, 90, 213, .2)
        &:not(:disabled):hover
            background: #454DC4
    &--secondary
        background: #fff
        color: #525AD5
        border-color: rgba(82, 90, 213, .28)
        &:not(:disabled):hover
            background: #EEF0FF
    &--danger
        background: #fff
        color: #E8183C
        border-color: rgba(232, 24, 60, .28)
        &:not(:disabled):hover
            background: #FFE8ED

.disable-modal-enter-active,
.disable-modal-leave-active
    transition: opacity .18s ease
    .disable-modal__dialog
        transition: transform .18s ease, opacity .18s ease

.disable-modal-enter,
.disable-modal-leave-to
    opacity: 0
    .disable-modal__dialog
        transform: translateY(8px) scale(.98)
        opacity: 0

@media (max-width: 560px)
    .disable-modal__actions
        grid-template-columns: 1fr
</style>
